// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, useBlocker, useNavigate } from "@tanstack/react-router";
import { Store } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useChainDetailQuery } from "@/api/hooks/use-chains";
import { PageHeader } from "@/components/layout/page-header";
import { IdPill } from "@/components/shared/id-pill";
import { KeyChip } from "@/components/shared/key-chip";
import { DetailPageSkeleton, PageProgressBar } from "@/components/shared/page-loading";
import { RequirePermission } from "@/components/shared/require-permission";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { UnsavedChangesDialog } from "@/components/shared/unsaved-changes-dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChainActionsMenu } from "@/features/chains/chain-actions-menu";
import { ChainActivityTab } from "@/features/chains/chain-activity-tab";
import { ChainEditTab, type ChainEditTabHandle } from "@/features/chains/chain-edit-tab";
import { ChainImportsTab } from "@/features/chains/chain-imports-tab";
import { ChainInfoTab } from "@/features/chains/chain-info-tab";
import { ChainRegionsTab } from "@/features/chains/chain-regions-tab";
import { ChainSettingsTab } from "@/features/chains/chain-settings-tab";
import { ChainStoresTab } from "@/features/chains/chain-stores-tab";
import { ChainUptimeTab } from "@/features/chains/chain-uptime-tab";
import { CompareChainDialog } from "@/features/chains/compare-chains-dialog";
import { CreateChainDialog } from "@/features/chains/create-chain-dialog";
import { CutOffTimesTab } from "@/features/cut-off-times/cut-off-times-tab";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { useDebugMode } from "@/lib/debug-mode";

export const Route = createFileRoute("/_authenticated/chains/$chainName")({
  validateSearch: z.object({ t: z.string().optional(), f: z.string().optional() }),
  component: () => (
    <RequirePermission permission={PAGE_PERMISSIONS.chains}>
      <ChainDetailPage />
    </RequirePermission>
  ),
});

function ChainDetailPage() {
  const { chainName } = Route.useParams();
  const { t: tab, f } = Route.useSearch();
  const { hasPermission } = useAuth();
  const { debugMode } = useDebugMode();
  const canEdit = hasPermission(PAGE_PERMISSIONS.chainsEdit);
  const canViewRegions = hasPermission(PAGE_PERMISSIONS.chainsRegions);
  const canViewImports = hasPermission(PAGE_PERMISSIONS.ingressImports);
  const canViewSettings = hasPermission(PAGE_PERMISSIONS.chainsSettings);
  const canViewCutOffTimes = hasPermission(PAGE_PERMISSIONS.chainsCutOffTimes);
  const canViewUptime = hasPermission(PAGE_PERMISSIONS.chainsUptime);
  // Activity log is work-in-progress — debug-mode only (in addition to the view permission).
  const canViewActivity = hasPermission(PAGE_PERMISSIONS.chainsActivity) && debugMode;
  const tabAllowed: Record<string, boolean> = {
    info: true,
    edit: canEdit,
    stores: true,
    regions: canViewRegions,
    "cut-off-times": canViewCutOffTimes,
    uptime: canViewUptime,
    imports: canViewImports,
    settings: canViewSettings,
    activity: canViewActivity,
  };
  const requestedTab = tab ?? "info";
  const activeTab = tabAllowed[requestedTab] ? requestedTab : "info";
  const navigate = useNavigate();
  const { t } = useTranslation("back-office");
  const { data: chain, isLoading, isError } = useChainDetailQuery(chainName);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const editTabRef = useRef<ChainEditTabHandle>(null);

  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirty,
    enableBeforeUnload: () => isFormDirty,
    withResolver: true,
  });

  function handleTabChange(tab: string) {
    navigate({
      to: "/chains/$chainName",
      params: { chainName },
      search: tab === "info" ? {} : { t: tab },
      replace: true,
    });
  }

  const { data: ctx } = useBackofficeContext();
  const displayName = chain?.legibleName ?? ctx?.chains?.find((c) => c.value === chainName)?.display ?? chainName;
  const breadcrumbs = useMemo(() => [{ label: t("chains.title"), to: "/chains" }], [t]);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);

  if (isLoading) {
    return (
      <div>
        <PageProgressBar />
        <DetailPageSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <PageHeader title={chainName} breadcrumbs={breadcrumbs} />
        <div className="p-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {t("errors.loadFailed")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={displayName}
        subtitle={
          <>
            {chain && <IdPill id={chain.chainId} />}
            <KeyChip label={chainName} variant="chain" />
            {chain && <StatusIndicator active={chain.isActive} />}
            {chain && (
              <Badge variant="secondary" className="gap-1.5 font-normal text-xs [&>svg]:size-2.5">
                <Store />
                {t("chains.storeCount", { count: chain.storesCount })}
              </Badge>
            )}
            <ChainActionsMenu
              onDuplicate={() => setCreateDialogOpen(true)}
              onCompareSettings={() => setCompareDialogOpen(true)}
              triggerClassName="ml-auto -my-1.5"
            />
          </>
        }
        breadcrumbs={breadcrumbs}
      />
      <div>
        <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
          <div className="w-full overflow-x-auto overflow-y-hidden pl-5 pr-6 pt-6">
            <TabsList>
              <TabsIndicator />
              <TabsTrigger value="info">{t("chains.tabs.info")}</TabsTrigger>
              {canEdit && <TabsTrigger value="edit">{t("chains.tabs.edit")}</TabsTrigger>}
              {canViewRegions && <TabsTrigger value="regions">{t("chains.tabs.regions")}</TabsTrigger>}
              <TabsTrigger value="stores">{t("chains.tabs.stores")}</TabsTrigger>
              {canViewCutOffTimes && <TabsTrigger value="cut-off-times">{t("chains.tabs.cutOffTimes")}</TabsTrigger>}
              {canViewUptime && <TabsTrigger value="uptime">{t("chains.tabs.uptime")}</TabsTrigger>}
              {canViewImports && <TabsTrigger value="imports">{t("chains.tabs.imports")}</TabsTrigger>}
              {canViewSettings && <TabsTrigger value="settings">{t("chains.tabs.settings")}</TabsTrigger>}
              {canViewActivity && <TabsTrigger value="activity">{t("chains.tabs.activityLog")}</TabsTrigger>}
            </TabsList>
          </div>
          <TabsContent value="info" className="space-y-4">
            <ChainInfoTab chainName={chainName} />
          </TabsContent>
          {canEdit && (
            <TabsContent value="edit" className="space-y-4">
              <ChainEditTab ref={editTabRef} chainName={chainName} onDirtyChange={setIsFormDirty} />
            </TabsContent>
          )}
          {canViewSettings && (
            <TabsContent value="settings" className="space-y-4">
              <ChainSettingsTab chainName={chainName} initialFilters={activeTab === "settings" ? f : undefined} />
            </TabsContent>
          )}
          {canViewRegions && (
            <TabsContent value="regions" className="space-y-4">
              <ChainRegionsTab chainName={chainName} />
            </TabsContent>
          )}
          <TabsContent value="stores" className="space-y-4">
            <ChainStoresTab chainName={chainName} initialFilters={activeTab === "stores" ? f : undefined} />
          </TabsContent>
          {canViewUptime && (
            <TabsContent value="uptime" className="space-y-4">
              <ChainUptimeTab chainName={chainName} initialFilters={activeTab === "uptime" ? f : undefined} />
            </TabsContent>
          )}
          {canViewImports && (
            <TabsContent value="imports" className="space-y-4">
              <ChainImportsTab chainName={chainName} initialFilters={activeTab === "imports" ? f : undefined} />
            </TabsContent>
          )}
          {canViewCutOffTimes && (
            <TabsContent value="cut-off-times" className="space-y-4">
              <CutOffTimesTab
                target={{ kind: "chain", chainName }}
                initialFilters={activeTab === "cut-off-times" ? f : undefined}
              />
            </TabsContent>
          )}
          {canViewActivity && (
            <TabsContent value="activity" className="space-y-4">
              <ChainActivityTab chainName={chainName} initialFilters={activeTab === "activity" ? f : undefined} />
            </TabsContent>
          )}
        </Tabs>
      </div>

      <CreateChainDialog
        key={`duplicate-${chainName}`}
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        duplicateFrom={{
          copyFrom: chainName,
          defaultTimeZone: chain?.defaultTimeZone ?? "",
        }}
      />

      <CompareChainDialog defaultChainName={chainName} open={compareDialogOpen} onOpenChange={setCompareDialogOpen} />

      <UnsavedChangesDialog
        open={blocker.status === "blocked"}
        onStay={() => blocker.reset?.()}
        onDiscard={() => {
          editTabRef.current?.reset();
          blocker.proceed?.();
        }}
      />
    </div>
  );
}
